import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../services/api';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

const steps = [
  { id: 1, title: 'Revisar Reserva', description: 'Confira os detalhes da estadia' },
  { id: 2, title: 'Dados do Hóspede', description: 'Informe suas informações pessoais' },
  { id: 3, title: 'Confirmar', description: 'Revise e finalize a reserva' }
];

const maskPhoneNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);

  if (digits.length === 0) return '';
  if (digits.length < 3) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length < 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

const extractDigits = (value) => String(value || '').replace(/\D/g, '');

const ReservationCompletionPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [formData, setFormData] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    originCity: '',
    documentNumber: '',
    specialRequests: '',
    adults: 1,
    children: 0,
    childAges: []
  });
  const [errors, setErrors] = useState({});
  const [completed, setCompleted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    if (token) {
      loadReservation();
    }
  }, [token]);

  const loadReservation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/reservations/public/completion/${token}`);
      const data = response.data;

      const effectiveChildren = Number(data.children) || 0;
      let childAgesArray = Array.isArray(data.childAges)
        ? data.childAges.map((age) => {
            if (age === null || age === undefined) return '';
            const numericAge = Number(age);
            return Number.isFinite(numericAge) ? String(numericAge) : '';
          })
        : [];

      if (childAgesArray.length < effectiveChildren) {
        childAgesArray = childAgesArray.concat(
          Array.from({ length: effectiveChildren - childAgesArray.length }, () => '')
        );
      } else if (childAgesArray.length > effectiveChildren) {
        childAgesArray = childAgesArray.slice(0, effectiveChildren);
      }

      setReservation(data);
      setFormData({
        guestName: data.guestName || '',
        guestEmail: data.guestEmail || '',
        guestPhone: maskPhoneNumber(data.guestPhone || ''),
        originCity: data.originCity || '',
        documentNumber: data.documentNumber || '',
        specialRequests: data.specialRequests || '',
        adults: Number(data.adults) || 1,
        children: effectiveChildren,
        childAges: childAgesArray
      });
    } catch (err) {
      console.error('Erro ao carregar reserva:', err);
      setError(err.response?.data?.message || 'Link inválido ou reserva não encontrada');
    } finally {
      setLoading(false);
    }
  };

  const nights = useMemo(() => {
    if (!reservation?.checkInDate || !reservation?.checkOutDate) return 1;
    const checkIn = new Date(reservation.checkInDate);
    const checkOut = new Date(reservation.checkOutDate);
    const diff = (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24);
    return diff > 0 ? Math.ceil(diff) : 1;
  }, [reservation?.checkInDate, reservation?.checkOutDate]);

  const minChildChargeAge = reservation?.minChildChargeAge !== null && reservation?.minChildChargeAge !== undefined
    ? Number(reservation.minChildChargeAge)
    : null;

  const childDiscountPercent = reservation?.childDiscountPercent !== null && reservation?.childDiscountPercent !== undefined
    ? Number(reservation.childDiscountPercent)
    : null;

  const hotelLogoHeight = useMemo(() => {
    if (!reservation?.hotel?.logoSize) return 80;
    const parsed = Number(reservation.hotel.logoSize);
    if (!Number.isFinite(parsed) || parsed <= 0) return 80;
    return Math.min(parsed, 120);
  }, [reservation?.hotel?.logoSize]);

  const childAgeNumbers = useMemo(() => {
    const agesArray = Array.isArray(formData.childAges) ? formData.childAges : [];
    return agesArray.map((age) => {
      if (age === '' || age === null || age === undefined) return null;
      const numeric = Number(age);
      return Number.isFinite(numeric) ? numeric : null;
    });
  }, [formData.childAges]);

  const chargedChildren = useMemo(() => {
    const totalChildren = Number(formData.children) || 0;
    if (!totalChildren) return 0;
    if (minChildChargeAge === null) return totalChildren;
    return childAgeNumbers.filter((age) => age !== null && age > minChildChargeAge).length;
  }, [formData.children, childAgeNumbers, minChildChargeAge]);

  const calculatedTotal = useMemo(() => {
    if (!reservation?.room || !reservation?.checkInDate || !reservation?.checkOutDate) return 0;
    const couplePrice = reservation.room?.couplePrice ? Number(reservation.room.couplePrice) : 0;
    const singlePrice = reservation.room?.singlePrice ? Number(reservation.room.singlePrice) : couplePrice;
    const adults = Number(formData.adults) || 1;
    
    // Calcular preço com desconto para crianças
    const childPriceMultiplier = childDiscountPercent != null && Number.isFinite(childDiscountPercent)
      ? (100 - childDiscountPercent) / 100
      : 1;
    const childSinglePrice = singlePrice * childPriceMultiplier;
    
    const totalPeople = Math.max(1, adults + chargedChildren);

    let nightlyPrice = couplePrice;
    if (totalPeople <= 1) {
      nightlyPrice = singlePrice;
    } else if (totalPeople === 2) {
      // Se há 2 adultos, usa preço casal
      // Se há 1 adulto + 1 criança cobrada, aplica desconto na criança
      if (adults === 2) {
        nightlyPrice = couplePrice;
      } else if (adults === 1 && chargedChildren === 1) {
        nightlyPrice = singlePrice + childSinglePrice;
      } else if (adults === 1 && chargedChildren === 0) {
        // 1 adulto + 1 criança não cobrada: usa preço solteiro
        nightlyPrice = singlePrice;
      } else {
        // Fallback: usa preço casal
        nightlyPrice = couplePrice;
      }
    } else {
      // 3 ou mais pessoas
      if (adults >= 2) {
        // 2 ou mais adultos: casal + extras
        const extraAdults = adults - 2;
        const extraChildren = chargedChildren;
        nightlyPrice = couplePrice + (extraAdults * singlePrice) + (extraChildren * childSinglePrice);
      } else {
        // 1 adulto + 2 ou mais crianças cobradas: solteiro + todas as crianças com desconto
        nightlyPrice = singlePrice + (chargedChildren * childSinglePrice);
      }
    }

    return nightlyPrice * nights;
  }, [reservation?.room, reservation?.checkInDate, reservation?.checkOutDate, formData.adults, chargedChildren, nights, childDiscountPercent]);

  const handleInputChange = (field, value, options = {}) => {
    if (field === 'documentNumber') {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 14);
      let masked = digits;
      if (digits.length <= 11) {
        masked = digits
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      } else {
        masked = digits
          .replace(/(\d{2})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1/$2')
          .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
      }
      value = masked;
    }

    if (field === 'adults') {
      const parsed = Math.max(1, parseInt(value || '0', 10));
      const sanitized = Number.isFinite(parsed) ? parsed : 1;
      setFormData((prev) => {
        const totalChildren = prev.children || 0;
        const childAges = Array.isArray(prev.childAges) ? [...prev.childAges] : [];
        if (childAges.length !== totalChildren) {
          const diff = totalChildren - childAges.length;
          if (diff > 0) childAges.push(...Array.from({ length: diff }, () => ''));
          if (diff < 0) childAges.splice(diff);
        }
        return {
          ...prev,
          adults: sanitized,
          childAges
        };
      });
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
      return;
    }

    if (field === 'guestPhone') {
      const maskedPhone = maskPhoneNumber(value);
      setFormData((prev) => ({
        ...prev,
        guestPhone: maskedPhone
      }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
      return;
    }

    if (field === 'children') {
      let parsed = parseInt(value || '0', 10);
      if (!Number.isFinite(parsed) || parsed < 0) parsed = 0;
      setFormData((prev) => {
        const adultsValue = prev.adults || 1;
        const childCount = parsed;
        let childAges = Array.isArray(prev.childAges) ? [...prev.childAges] : [];
        if (childCount > childAges.length) {
          const diff = childCount - childAges.length;
          childAges.push(...Array.from({ length: diff }, () => ''));
        } else if (childCount < childAges.length) {
          childAges = childAges.slice(0, childCount);
        }
        return {
          ...prev,
          children: childCount,
          childAges,
          adults: adultsValue
        };
      });
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
      return;
    }

    if (field === 'childAges') {
      const { index } = options;
      setFormData((prev) => {
        const childAges = Array.isArray(prev.childAges) ? [...prev.childAges] : [];
        if (index >= 0 && index < childAges.length) {
          const sanitized = value === '' ? '' : String(value).replace(/\D/g, '').slice(0, 2);
          childAges[index] = sanitized;
        }
        return {
          ...prev,
          childAges
        };
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateCurrentStep = () => {
    if (currentStep !== 2) return true;

    const newErrors = {};
    if (!formData.guestName.trim()) newErrors.guestName = 'Nome é obrigatório';
    if (!formData.guestPhone.trim()) newErrors.guestPhone = 'Telefone é obrigatório';
    if (!formData.documentNumber.trim()) newErrors.documentNumber = 'CPF/RG é obrigatório';
    if (!formData.adults || Number(formData.adults) < 1) newErrors.adults = 'Informe ao menos 1 adulto';
    if (Number(formData.children) > 0) {
      const childAges = Array.isArray(formData.childAges) ? formData.childAges : [];
      const missingAgeIndex = childAges.findIndex((age) => age === '' || age === null || age === undefined);
      if (missingAgeIndex !== -1) {
        newErrors.childAges = 'Informe a idade de todas as crianças';
      }
    }

    if (formData.guestEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guestEmail)) {
      newErrors.guestEmail = 'E-mail inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (!validateCurrentStep()) return;
    setCurrentStep(prev => Math.min(prev + 1, steps.length));
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    try {
      setSubmitting(true);
      setError(null);
      const documentNumberDigits = formData.documentNumber.replace(/\D/g, '');

      await api.put(`/reservations/public/completion/${token}`, {
        guestName: formData.guestName.trim(),
        guestEmail: formData.guestEmail.trim(),
        guestPhone: extractDigits(formData.guestPhone),
        originCity: formData.originCity.trim(),
        documentNumber: documentNumberDigits,
        specialRequests: formData.specialRequests.trim() || null,
        adults: Number(formData.adults) || 1,
        children: Number(formData.children) || 0,
        childAges: Array.isArray(formData.childAges)
          ? formData.childAges.map((age) => {
              if (age === '' || age === null || age === undefined) return null;
              const sanitized = Number(age);
              return Number.isFinite(sanitized) ? sanitized : null;
            })
          : [],
        totalAmount: Number(calculatedTotal.toFixed(2))
      });

      setCompleted(true);
    } catch (err) {
      console.error('Erro ao completar reserva:', err);
      setError(err.response?.data?.message || 'Erro ao salvar dados. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const renderStepContent = () => {
    if (!reservation) return null;

    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="bg-muted/30 border border-border rounded-lg p-5">
              <h3 className="text-base font-semibold text-foreground mb-4">Detalhes da reserva</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground block mb-1">Código</span>
                  <span className="font-medium text-foreground">{reservation.reservationCode}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Quarto</span>
                  <span className="font-medium text-foreground">{reservation.room?.number || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Check-in</span>
                  <span className="font-medium text-foreground">
                    {reservation.checkInDate ? `${formatDate(reservation.checkInDate)} às ${new Date(reservation.checkInDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Check-out</span>
                  <span className="font-medium text-foreground">
                    {reservation.checkOutDate ? `${formatDate(reservation.checkOutDate)} às ${new Date(reservation.checkOutDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Hospedagem</span>
                  <span className="font-medium text-foreground">{formData.adults || 1} adulto(s){(Number(formData.children) > 0) ? ` • ${formData.children} criança(s)` : ''}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Duração</span>
                  <span className="font-medium text-foreground">{nights} noite(s)</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Valor estimado</span>
                  <span className="font-medium text-foreground">R$ {calculatedTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground block mb-1">Hotel</span>
                  <span className="font-medium text-foreground">{reservation.hotel?.name || 'Hotel'}</span>
                </div>
              </div>
            </div>
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 text-xs text-success flex items-start gap-3">
              <Icon name="Info" size={14} className="mt-0.5" />
              <span>Verifique as informações acima. Caso algo esteja incorreto, entre em contato com o hotel antes de prosseguir.</span>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Nome completo *"
                type="text"
                value={formData.guestName}
                onChange={(e) => handleInputChange('guestName', e.target.value)}
                error={errors.guestName}
              />
              <Input
                label="Telefone *"
                type="text"
                value={formData.guestPhone}
                onChange={(e) => handleInputChange('guestPhone', e.target.value)}
                error={errors.guestPhone}
              />
              <Input
                label="CPF/CNPJ *"
                type="text"
                value={formData.documentNumber}
                onChange={(e) => handleInputChange('documentNumber', e.target.value)}
                error={errors.documentNumber}
              />
              <Input
                label="E-mail"
                type="email"
                value={formData.guestEmail}
                onChange={(e) => handleInputChange('guestEmail', e.target.value)}
                error={errors.guestEmail}
              />
              <Input
                label="Cidade de origem"
                type="text"
                value={formData.originCity}
                onChange={(e) => handleInputChange('originCity', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Número de adultos *"
                type="number"
                min={1}
                value={formData.adults}
                onChange={(e) => handleInputChange('adults', e.target.value)}
                error={errors.adults}
              />
              <div>
                <Input
                  label="Número de crianças"
                  type="number"
                  min={0}
                  value={formData.children}
                  onChange={(e) => handleInputChange('children', e.target.value)}
                />
                {minChildChargeAge !== null && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Crianças com idade superior a {minChildChargeAge} ano(s) são cobradas na diária.
                  </p>
                )}
              </div>
            </div>
            {Number(formData.children) > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Icon name="Users" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">Idades das crianças</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {(Array.isArray(formData.childAges) ? formData.childAges : []).map((age, idx) => (
                    <Input
                      key={idx}
                      label={`Criança ${idx + 1}`}
                      type="number"
                      min={0}
                      value={age}
                      onChange={(e) => handleInputChange('childAges', e.target.value, { index: idx })}
                    />
                  ))}
                </div>
                {errors.childAges && (
                  <p className="text-xs text-destructive">{errors.childAges}</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Solicitações especiais</label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Informe necessidades específicas, preferências ou observações"
                value={formData.specialRequests}
                onChange={(e) => handleInputChange('specialRequests', e.target.value)}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="bg-muted/20 border border-border rounded-lg p-5">
              <h3 className="text-base font-semibold text-foreground mb-4">Resumo da reserva</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground block mb-1">Hóspede</span>
                  <span className="font-medium text-foreground">{formData.guestName || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Contato</span>
                  <span className="font-medium text-foreground">{formData.guestPhone || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Documento</span>
                  <span className="font-medium text-foreground">{formData.documentNumber || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">E-mail</span>
                  <span className="font-medium text-foreground">{formData.guestEmail || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Cidade de origem</span>
                  <span className="font-medium text-foreground">{formData.originCity || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Adultos</span>
                  <span className="font-medium text-foreground">{formData.adults || 1}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Crianças</span>
                  <span className="font-medium text-foreground">{formData.children || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Check-in</span>
                  <span className="font-medium text-foreground">{reservation.checkInDate ? `${formatDate(reservation.checkInDate)} às ${new Date(reservation.checkInDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Check-out</span>
                  <span className="font-medium text-foreground">{reservation.checkOutDate ? `${formatDate(reservation.checkOutDate)} às ${new Date(reservation.checkOutDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">Total</span>
                  <span className="font-medium text-foreground">R$ {calculatedTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                {Number(formData.children) > 0 && (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground block mb-1">Idades das crianças</span>
                    <span className="font-medium text-foreground">
                      {(Array.isArray(formData.childAges) ? formData.childAges : []).filter((age) => age !== '').join(', ') || '—'}
                    </span>
                  </div>
                )}
                {formData.specialRequests && (
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground block mb-1">Solicitações especiais</span>
                    <span className="font-medium text-foreground">{formData.specialRequests}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-xs text-primary flex items-start gap-3">
              <Icon name="Shield" size={14} className="mt-0.5" />
              <span>Ao concluir, seus dados serão enviados para o hotel e a reserva estará confirmada.</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderActions = () => {
    if (currentStep === steps.length) {
      return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <Button variant="outline" onClick={handlePrevStep} disabled={submitting} className="w-full sm:w-auto">
            Voltar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="w-full sm:w-auto">
            {submitting ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Finalizando...</span>
              </div>
            ) : (
              'Confirmar reserva'
            )}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <Button variant="outline" onClick={handlePrevStep} disabled={currentStep === 1 || submitting} className="w-full sm:w-auto">
          Voltar
        </Button>
        <Button onClick={handleNextStep} disabled={submitting} className="w-full sm:w-auto">
          Próximo
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando dados da reserva...</p>
        </div>
      </div>
    );
  }

  if (error && !reservation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Link inválido</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-success" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Reserva confirmada!</h2>
          <p className="text-muted-foreground mb-4">
            Seus dados foram enviados com sucesso. Aguardamos você no check-in!
          </p>
          <p className="text-sm text-muted-foreground">
            Código da reserva: <strong>{reservation?.reservationCode}</strong>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-card border border-border rounded-lg p-6">
          {reservation?.hotel?.logo && (
            <div className="flex justify-center mb-4">
              <img
                src={reservation.hotel.logo}
                alt={reservation.hotel.name || 'Logo do hotel'}
                className="object-contain max-w-[160px]"
                style={{ maxHeight: `${hotelLogoHeight}px` }}
              />
            </div>
          )}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={22} className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Complete sua reserva</h1>
              <p className="text-sm text-muted-foreground">{reservation?.hotel?.name || 'Hotel'}</p>
            </div>
          </div>

          {/* Steps indicator */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Etapa {currentStep} de {steps.length}</span>
              <span>·</span>
              <span>{steps[currentStep - 1]?.description}</span>
            </div>
            <div className="flex items-center gap-2">
              {steps.map((step) => {
                const isActive = step.id === currentStep;
                const isCompleted = step.id < currentStep;
                return (
                  <div key={step.id} className={`flex items-center gap-2 ${step.id === steps.length ? '' : 'mr-1'}`}>
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                        isCompleted
                          ? 'bg-primary text-primary-foreground'
                          : isActive
                            ? 'bg-primary/10 text-primary border border-primary/30'
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {step.id}
                    </div>
                    {step.id !== steps.length && (
                      <div className={`h-[2px] w-10 ${isCompleted ? 'bg-primary' : 'bg-muted'}`}></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border border-border rounded-lg bg-card p-6 space-y-6">
            {renderStepContent()}

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            {renderActions()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationCompletionPage;

